<template>
  <template v-if="isContentReady">
    <CadyNotification
      v-if="notificationForDisplay"
      :notification="notificationForDisplay"
      @dismiss="handleNotificationDismissed(notificationForDisplay)"
    />
    <div class="container cady-header-container">
      <CadyHeader
        v-if="!isCustomerServiceView"
        :navItems="mainNavItems"
        :totalCartQuantity="totalCartQuantity"
        signInHref="https://store.cady.com/login"
        :signInNewTab="true"
        @cart-click="handleCartClick"
      >
        <!-- Both slots mount at every width, so gate each sprite on its breakpoint: a CSS-hidden
             one still auto-opens its teleported card, pinned to the top-left corner. -->
        <template #before-cart>
          <div
            v-if="headerPriceRuleSprite && mq$.lgPlus"
            class="app-layout__header-sprite d-none d-lg-flex"
          >
            <PriceRuleSprite
              :sprite="headerPriceRuleSprite"
              :context="priceRuleSpriteContext.AppHeader"
            />
          </div>
        </template>
        <template #mobile-before-cart>
          <div
            v-if="headerPriceRuleSprite && !mq$.lgPlus"
            class="app-layout__header-sprite-mobile"
          >
            <PriceRuleSprite
              :sprite="headerPriceRuleSprite"
              :context="priceRuleSpriteContext.AppHeader"
            />
          </div>
        </template>
      </CadyHeader>

      <AppTopNavCustomerService v-if="isCustomerServiceView" />
    </div>
    <main
      role="main"
      class="app-main"
      :class="{
        'app-main--white': hasWhiteContentBackground,
        'app-main--black': hasBlackContentBackground,
        'app-main--no-bottom-margin': hasDarkDisclaimerVariant
      }"
    >
      <RouterView />
    </main>
    <div
      :class="[
        'app-layout__below-main',
        { 'app-layout__below-main--dark': hasDarkDisclaimerVariant }
      ]"
    >
      <AppDisclaimers
        class="container"
        :variant="disclaimerVariant"
      />
      <FooterKlarnaBlurb
        class="container"
        :variant="disclaimerVariant"
      />
    </div>
    <AppFooter />
    <ModalSampleAchievementVideo
      v-if="mainStore.showModalAchievementVideo"
      @close="mainStore.showModalAchievementVideo = false"
    />
  </template>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useMq } from 'vue3-mq'
import { useRoute } from 'vue-router'

import { useAppContentStore } from '@/stores/appContent'
import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'
import { useMainStore } from '@/stores/main'
import { mapState, mapStores } from 'pinia'

import contentfulResolveResponse from 'contentful-resolve-response'

import Sunny16CommerceContentService from '@/services/Sunny16CommerceContentService'

import PriceRuleSprite, { PriceRuleSpriteContext } from '@/components/PriceRule/PriceRuleSprite.vue'
import AppDisclaimers, { DisclaimerVariant } from '@/components/Shared/AppDisclaimers.vue'
import CadyNotification from '@/components/Shared/CadyNotification.vue'
import FooterKlarnaBlurb from '@/components/Shared/FooterKlarnaBlurb.vue'
import ModalSampleAchievementVideo from '@/components/Shared/ModalSampleAchievementVideo.vue'

import { CadyHeader, type HeaderNavItem } from '@VelocitySquared/cady-design-system'

import AppFooter from './AppFooter/AppFooter.vue'
import AppTopNavCustomerService from './AppTopNavCustomerService.vue'

export default defineComponent({
  name: 'AppLayout',
  setup() {
    return {
      mq$: useMq(),
      route: useRoute()
    }
  },
  components: {
    AppDisclaimers,
    AppFooter,
    AppTopNavCustomerService,
    CadyHeader,
    CadyNotification,
    FooterKlarnaBlurb,
    ModalSampleAchievementVideo,
    PriceRuleSprite
  },
  data() {
    return {
      isContentReady: false
    }
  },
  computed: {
    ...mapStores(useMainStore, useCartStore, useCatalogStore, useAppContentStore),
    ...mapState(useMainStore, ['showModalAchievementVideo']),
    ...mapState(useCatalogStore, ['notificationForDisplay', 'headerPriceRuleSprite']),
    // Hierarchical top nav (BugHerd #64) — structure in code, item text/links from
    // content; pass the current market so the active child is highlighted.
    mainNavItems(): HeaderNavItem[] {
      return this.appContentStore.mainNavTree(this.mainStore.cadyMarket)
    },
    priceRuleSpriteContext() {
      return PriceRuleSpriteContext
    },
    totalCartQuantity(): number {
      return this.cartStore.totalCartQuantity
    },
    isCustomerServiceView(): boolean {
      return (
        (this.route.meta.isCustomerServiceView !== undefined &&
          this.route.meta.isCustomerServiceView === true) ||
        this.cartStore.isCustomerServiceCart
      )
    },
    // Content area is off-white by default; routes opt out via `meta.whiteContentBackground`.
    hasWhiteContentBackground(): boolean {
      return this.route.meta.whiteContentBackground === true
    },
    hasBlackContentBackground(): boolean {
      return this.route.meta.blackContentBackground === true
    },
    // AppDisclaimers' visual treatment; routes opt in via `meta.disclaimerVariant`.
    disclaimerVariant(): DisclaimerVariant {
      return this.route.meta.disclaimerVariant ?? DisclaimerVariant.Default
    },
    // Drives the shared black backdrop behind AppDisclaimers + FooterKlarnaBlurb — everything
    // under the `.app-main` gradient line, down to the (always-black) AppFooter.
    hasDarkDisclaimerVariant(): boolean {
      return this.disclaimerVariant === DisclaimerVariant.Dark
    }
  },
  async beforeMount() {
    if (!this.appContentStore.globalAppContent) {
      const sunny16CommerceContentService = new Sunny16CommerceContentService()
      const siteContentResponse = await sunny16CommerceContentService.getArbitraryContent(
        'contentModule',
        'app-global-content'
      )

      this.appContentStore.setGlobalAppContent(
        contentfulResolveResponse(siteContentResponse)[0].fields
      )
    }

    this.isContentReady = true
  },
  methods: {
    handleNotificationDismissed(notification) {
      this.catalogStore.dismissNotification(notification)
    },
    handleCartClick() {
      this.mainStore.toggleCartSidebar()
    }
  }
})
</script>

<style scoped lang="scss">
.cady-header-container {
  padding-left: 0;
  padding-right: 0;

  // The design-system `CadyHeader` paints a faint hairline (`#f5f5f5`) under itself via an
  // `::after` pseudo-element. Drop it on desktop, where it reads as an unwanted line between the
  // header and the storefront top bar.
  @include media-breakpoint-up(lg) {
    :deep(.cady-header--styled)::after {
      display: none;
    }
  }
}

// Single source of the off-white content backdrop: sits below the header (and the in-view
// storefront top bar), leaving the surrounding page chrome (header strip, footer) on white.
.app-main {
  position: relative;
  background-color: $off-white;
  // BugHerd #112: anchor the footer separation here. The primary content view is the
  // full-width element directly above the footer band, so a bottom padding + gradient rule
  // pinned to its bottom edge gives the requested 56px gap and border without touching the
  // footer's contained `.container` pieces. The rule overlaps the bottom of the padding box,
  // so add its height to keep a true 56px of clear space above it.
  padding-bottom: calc(3.5rem + 7px); // 56px above the 7px gradient rule
  // Gap between the content view (ending at its gradient rule) and the
  // disclaimers below: tight on mobile, generous on desktop.
  margin-bottom: 1rem;

  @include media-breakpoint-up(md) {
    margin-bottom: 5.5rem;
  }

  &::after {
    content: '';
    position: absolute;
    inset: auto 0 0 0;
    height: 7px;
    background: linear-gradient(90deg, #514c9a, #ad4091);
  }

  &--white {
    background-color: #fff;
  }

  &--black {
    background-color: #000000;
    color: #ffffff;
  }

  // When AppDisclaimers/FooterKlarnaBlurb go dark (see `.app-layout__below-main--dark`), that
  // gap moves to padding-top on the wrapper below instead, so the space stays black rather than
  // showing the page's white backdrop between this element's box and the wrapper's.
  &--no-bottom-margin {
    margin-bottom: 0;
  }
}

// Wraps AppDisclaimers + FooterKlarnaBlurb — everything under the `.app-main` gradient line,
// down to the (always-black) AppFooter.
.app-layout__below-main {
  &--dark {
    background-color: #000000;
    padding-top: 1rem;
    // Guards against the last child's margin-bottom collapsing through this element's own
    // bottom edge, which would leave an uncovered sliver of the page's white backdrop between
    // this box and AppFooter.
    padding-bottom: 0.1px;

    @include media-breakpoint-up(md) {
      padding-top: 5.5rem;
    }
  }
}

// Mobile top-bar instance (always-visible, before the cart).
.app-layout__header-sprite-mobile {
  display: flex;
  align-items: center;
  margin-right: 0.75rem;
}

.app-layout__header-sprite {
  display: flex;
  align-items: center;
  // Mobile: the slot stacks inside the collapsed menu; give it breathing room.
  margin-top: 1rem;

  @include media-breakpoint-up(lg) {
    // Desktop: sits between the sign-in link and the cart. The sign-in section
    // already carries right padding, so we only need a gap on the cart side.
    margin-top: 0;
    margin-right: 1.75rem;
    align-self: center;
    // The adjacent nav/sign-in items are pushed down by top padding; nudge the
    // centered badge down to sit on the same optical line.
    transform: translateY(3px);
  }
}
</style>
